import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AppSettings } from '../shared/app-settings';

declare global {
  interface Window {
    clarity: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ClarityService {
  private initialized = false;

  constructor() {}

  /**
   * Initialize Microsoft Clarity
   * @param projectId - The Clarity project ID (optional, will try to get from client config)
   */
  initialize(projectId?: string): void {
    if (this.initialized) {
      console.log('Clarity already initialized');
      return;
    }

    let clarityProjectId = projectId;

    // If no project ID provided, try to get from client configuration
    if (!clarityProjectId) {
      clarityProjectId = this.getClarityProjectIdFromConfig();
    }

    if (!clarityProjectId) {
      console.log('Clarity project ID not found. Skipping Clarity initialization.');
      return;
    }

    try {
      // Load Clarity script
      this.loadClarityScript(clarityProjectId);
      this.initialized = true;
      console.log('Microsoft Clarity initialized with project ID:', clarityProjectId);
    } catch (error) {
      console.error('Failed to initialize Microsoft Clarity:', error);
    }
  }

  /**
   * Get Clarity project ID from client configuration stored in localStorage
   */
  private getClarityProjectIdFromConfig(): string | null {
    try {
      const clientConfigString = localStorage.getItem(AppSettings.StorageKeys.ClientConfig);
      if (clientConfigString) {
        const clientConfig = JSON.parse(clientConfigString);
        return clientConfig?.clarityProjectId || null;
      }
    } catch (error) {
      console.error('Error reading client config for Clarity project ID:', error);
    }
    return null;
  }

  /**
   * Load Clarity script dynamically
   */
  private loadClarityScript(projectId: string): void {
    // Add Clarity script to head
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${projectId}");
    `;
    document.head.appendChild(script);
  }

  /**
   * Identify a user to Clarity (useful for tracking specific users)
   * @param userId - Unique user identifier
   * @param sessionId - Optional session identifier
   * @param pageId - Optional page identifier
   * @param friendlyName - Optional friendly name for the user
   */
  identify(userId: string, sessionId?: string, pageId?: string, friendlyName?: string): void {
    if (!this.initialized || !window.clarity) {
      console.warn('Clarity not initialized. Cannot identify user.');
      return;
    }

    try {
      window.clarity('identify', userId, sessionId, pageId, friendlyName);
      console.log('Clarity user identified:', userId);
    } catch (error) {
      console.error('Error identifying user to Clarity:', error);
    }
  }

  /**
   * Set custom tags for Clarity sessions
   * @param key - Tag key
   * @param value - Tag value
   */
  setTag(key: string, value: string): void {
    if (!this.initialized || !window.clarity) {
      console.warn('Clarity not initialized. Cannot set tag.');
      return;
    }

    try {
      window.clarity('set', key, value);
      console.log('Clarity tag set:', key, value);
    } catch (error) {
      console.error('Error setting Clarity tag:', error);
    }
  }

  /**
   * Trigger custom events in Clarity
   * @param eventName - Name of the event
   */
  event(eventName: string): void {
    if (!this.initialized || !window.clarity) {
      console.warn('Clarity not initialized. Cannot trigger event.');
      return;
    }

    try {
      window.clarity('event', eventName);
      console.log('Clarity event triggered:', eventName);
    } catch (error) {
      console.error('Error triggering Clarity event:', error);
    }
  }

  /**
   * Stop Clarity tracking
   */
  stop(): void {
    if (!this.initialized || !window.clarity) {
      console.warn('Clarity not initialized. Cannot stop tracking.');
      return;
    }

    try {
      window.clarity('stop');
      console.log('Clarity tracking stopped');
    } catch (error) {
      console.error('Error stopping Clarity tracking:', error);
    }
  }

  /**
   * Check if Clarity is initialized and available
   */
  isAvailable(): boolean {
    return this.initialized && typeof window.clarity !== 'undefined';
  }

  /**
   * Re-initialize Clarity with a new project ID (useful for switching customers)
   * @param projectId - New Clarity project ID
   */
  reinitialize(projectId: string): void {
    this.initialized = false;
    this.initialize(projectId);
  }
}
